// apps/spa/src/components/audit/AuditLogDiffPanel.tsx
// Expandable diff panel for an audit log entry.
//
// Renders inline beneath the expanded AuditLogRow (full-row spanning cell).
// Per action type:
//   *.create      — shows 'after' payload as key-value list + embed preview for reminder/command
//   *.update      — shows changedFields list + before/after table for only changed fields
//   *.delete      — shows full 'before' payload as key-value list
//   post_now / dispatch / dm_test — shows success, discordMessageId?, error?, retryAfter?
//   auth.login / auth.logout      — shows ip?, userAgent?; "(no metadata)" if absent

import type { EnrichedAuditLogEntry, Embed } from '@guardian/shared';
import { DiscordEmbedPreview } from '@/components/EmbedEditor/DiscordEmbedPreview';

interface AuditLogDiffPanelProps {
  entry: EnrichedAuditLogEntry;
}

export function AuditLogDiffPanel({ entry }: AuditLogDiffPanelProps) {
  const payload = entry.payloadJson ? (JSON.parse(entry.payloadJson) as Record<string, unknown>) : {};

  // *.create — show 'after' payload as key-value list
  if (entry.action.endsWith('.create')) {
    const data = (payload.after as Record<string, unknown>) ?? payload;
    return <KeyValueList data={data} />;
  }

  // *.update — show changedFields + before/after table for changed fields only
  if (entry.action.endsWith('.update')) {
    const changedFields = (payload.changedFields as string[]) ?? [];
    const before = (payload.before as Record<string, unknown>) ?? {};
    const after = (payload.after as Record<string, unknown>) ?? {};
    return (
      <div className="space-y-3 text-sm">
        {changedFields.length > 0 && (
          <div className="text-muted-foreground">
            Changed fields: <span className="text-foreground font-medium">{changedFields.join(', ')}</span>
          </div>
        )}
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b">
              <th className="text-left py-1 pr-4 font-medium text-muted-foreground">Field</th>
              <th className="text-left py-1 pr-4 font-medium text-muted-foreground">Before</th>
              <th className="text-left py-1 font-medium text-muted-foreground">After</th>
            </tr>
          </thead>
          <tbody>
            {changedFields.map((f) => (
              <tr key={f} className="border-b last:border-0">
                <td className="py-1 pr-4 font-medium">{f}</td>
                <td className="py-1 pr-4 text-muted-foreground">{JSON.stringify(before[f])}</td>
                <td className="py-1">{JSON.stringify(after[f])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // *.delete — show full 'before' payload
  if (entry.action.endsWith('.delete')) {
    const data = (payload.before as Record<string, unknown>) ?? payload;
    return <KeyValueList data={data} />;
  }

  // post_now / dispatch / dm_test — show delivery outcome fields
  if (
    entry.action === 'reminder.post_now' ||
    entry.action === 'reminder.dispatch' ||
    entry.action === 'reminder.dm_test'
  ) {
    const p = payload as {
      success?: boolean;
      discordMessageId?: string;
      error?: string;
      retryAfter?: number;
      recipient_id?: string;
      discord_error_code?: number;
    };
    return (
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Success</dt>
        <dd className={p.success ? 'text-green-600' : 'text-red-500'}>{String(p.success ?? false)}</dd>
        {p.discordMessageId && (
          <>
            <dt className="text-muted-foreground">Discord message ID</dt>
            <dd className="font-mono text-xs">{p.discordMessageId}</dd>
          </>
        )}
        {p.recipient_id && (
          <>
            <dt className="text-muted-foreground">Recipient</dt>
            <dd className="font-mono text-xs">{p.recipient_id}</dd>
          </>
        )}
        {p.error && (
          <>
            <dt className="text-muted-foreground">Error</dt>
            <dd className="text-red-500">{p.error}</dd>
          </>
        )}
        {p.discord_error_code !== undefined && (
          <>
            <dt className="text-muted-foreground">Discord error code</dt>
            <dd className="font-mono text-xs">{p.discord_error_code}</dd>
          </>
        )}
        {p.retryAfter !== undefined && (
          <>
            <dt className="text-muted-foreground">Retry after</dt>
            <dd>{p.retryAfter}s</dd>
          </>
        )}
      </dl>
    );
  }

  // auth.login / auth.logout — show ip/userAgent if present
  if (entry.action.startsWith('auth.')) {
    const p = payload as { ip?: string; userAgent?: string };
    if (!p.ip && !p.userAgent) {
      return <p className="text-sm text-muted-foreground">(no metadata)</p>;
    }
    return (
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        {p.ip && (
          <>
            <dt className="text-muted-foreground">IP</dt>
            <dd className="font-mono text-xs">{p.ip}</dd>
          </>
        )}
        {p.userAgent && (
          <>
            <dt className="text-muted-foreground">User agent</dt>
            <dd className="break-all text-xs">{p.userAgent}</dd>
          </>
        )}
      </dl>
    );
  }

  // Fallback — raw JSON for unknown action types
  return (
    <pre className="text-xs bg-muted/50 p-2 rounded overflow-auto max-h-40">
      {JSON.stringify(payload, null, 2)}
    </pre>
  );
}

// ---------------------------------------------------------------------------
// KeyValueList — renders payload data as a DL + optional embed preview
// ---------------------------------------------------------------------------

interface KeyValueListProps {
  data: Record<string, unknown>;
}

function KeyValueList({ data }: KeyValueListProps) {
  // Extract embedJson / embed for the Discord preview (reminder + command entities)
  let embed: Record<string, unknown> | null = null;
  try {
    if (typeof data.embedJson === 'string') {
      embed = JSON.parse(data.embedJson) as Record<string, unknown>;
    } else if (data.embed && typeof data.embed === 'object') {
      embed = data.embed as Record<string, unknown>;
    }
  } catch {
    // malformed embedJson — skip preview
  }

  // Render all other fields (exclude embedJson/embed from the KV table to avoid JSON blob clutter)
  const otherEntries = Object.entries(data).filter(
    ([k]) => k !== 'embedJson' && k !== 'embed',
  );

  return (
    <div className="space-y-3">
      {otherEntries.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          {otherEntries.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="break-all">
                {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {embed && (
        <div>
          <div className="text-xs text-muted-foreground mb-1">Embed preview</div>
          <DiscordEmbedPreview embed={embed as Embed} />
        </div>
      )}
    </div>
  );
}
